// Radar chart for the BMD report. Each axis is a CLUSTER, valued at the score
// the student got across it.
//
// It used to plot main_topics in "Base test" mode under a heading that said
// Cluster Strength, so the axes and the label disagreed: a topic name like
// "Fractions (including Decimals)" appeared where the cluster is "Fractions,
// Decimals, Percentages, Ratio". Both modes are clusters now, and the mode
// changes the SCOPE rather than the axes.
//
// Fed from the same mastery_tree the Mastery Breakdown tab reads, so the two
// cannot report different percentages for the same cluster.
//
// Chart.js registration is local to this component so the radial scale is not
// registered globally.

import React from "react"
import { Radar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js"
import type { BmdMasteryCluster } from "../types/bmd-report.types"

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
)

export type BmdTopicStrengthRadarProps = {
  masteryTree: BmdMasteryCluster[]
  yearLevelName?: string
}

// Both modes plot the same cluster axes. "Base test" is the assigned test
// alone; "Overall" adds the prerequisite test. Fixed axes are the point: the
// two are comparable now, which they were not when one drew topics and the
// other drew clusters.
//
// Defaults to "overall" at the PM's request: the prerequisite test is where
// most of a struggling student's answers end up, so the base-only radar was
// showing a picture nobody wanted to open on. The naming matches the same
// toggle on the KPI cards, Mastery Breakdown and Skills Gap tabs.
type RadarMode = "base" | "overall"

export const BmdTopicStrengthRadar: React.FC<BmdTopicStrengthRadarProps> = ({
  masteryTree,
  yearLevelName,
}) => {
  const [mode, setMode] = React.useState<RadarMode>("overall")
  const isOverall = mode === "overall"

  const points = masteryTree.map((c) => ({
    label: c.strand_name,
    score_pct: (isOverall ? c.overall : c.base).score_pct,
  }))

  const modeSelect = (
    <select
      className="form-select form-select-sm"
      style={{ width: "auto" }}
      value={mode}
      onChange={(e) => setMode(e.target.value as RadarMode)}
      aria-label="Radar scope"
    >
      <option value="base">Base test</option>
      <option value="overall">Overall (with prerequisite test)</option>
    </select>
  )

  if (points.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="text-uppercase text-muted font-size-12">
            Cluster Strength
            {yearLevelName ? `: ${yearLevelName} BASE LEVEL` : ""}
          </div>
          {modeSelect}
        </div>
        <p className="text-muted mb-0">
          No answered questions yet at this year level.
        </p>
      </div>
    )
  }

  const data = {
    labels: points.map((p) => `${p.label}  ${p.score_pct}%`),
    datasets: [
      {
        label: "Score",
        data: points.map((p) => p.score_pct),
        backgroundColor: "rgba(220, 53, 69, 0.15)",
        borderColor: "rgba(220, 53, 69, 0.85)",
        borderWidth: 2,
        pointBackgroundColor: "rgba(220, 53, 69, 1)",
        pointRadius: 4,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 25,
          backdropColor: "transparent",
          color: "#9ca3af",
        },
        pointLabels: {
          font: { size: 12, weight: "bold" as const },
          color: "#1f2937",
        },
        grid: { color: "rgba(0,0,0,0.06)" },
        angleLines: { color: "rgba(0,0,0,0.06)" },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { dataIndex: number; parsed: { r: number } }) => {
            const p = points[ctx.dataIndex]
            return `${p.label}: ${ctx.parsed.r}%`
          },
        },
      },
    },
  }

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div className="text-uppercase text-muted font-size-12">
          Cluster Strength
          {yearLevelName ? `: ${yearLevelName} BASE LEVEL` : ""}
        </div>
        {modeSelect}
      </div>
      {isOverall ? (
        <p className="text-muted font-size-12 mb-2">
          Base test and prerequisite test combined, grouped by cluster.
        </p>
      ) : null}
      <div style={{ height: "420px" }}>
        <Radar data={data} options={options} />
      </div>
    </div>
  )
}

export default BmdTopicStrengthRadar
